/**
 * Wishlist: localStorage only, no backend/app.
 *
 * A saved product's full display data (handle, title, price, image, url) is
 * captured straight off the card's own `data-*` attributes at the moment the
 * heart is clicked, and that snapshot is what gets stored — not just an id.
 * That is what lets the drawer render a list of saved products without a
 * network request: there is nothing to look up, the data already travelled
 * with the toggle.
 *
 * Same vanilla-custom-element style as `cart-drawer`/`search-drawer`: no
 * framework, `hidden` drives open/close, a delegated document click binds
 * every trigger regardless of when its markup showed up on the page.
 */
(function () {
  var STORAGE_KEY = 'pixelbear:wishlist';

  function readList() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (error) {
      return [];
    }
  }

  function writeList(list) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (error) {
      // Private browsing / storage disabled — the toggle still works for the
      // current page view, it just won't persist. Nothing to do here.
    }
  }

  function isSaved(handle) {
    if (!handle) return false;
    return readList().some(function (item) {
      return item.handle === handle;
    });
  }

  /** Adds or removes a product, keyed by handle, and tells the page about it. */
  function toggle(detail) {
    var list = readList();
    var index = -1;

    for (var i = 0; i < list.length; i++) {
      if (list[i].handle === detail.handle) {
        index = i;
        break;
      }
    }

    var saved;
    if (index > -1) {
      list.splice(index, 1);
      saved = false;
    } else {
      list.push(detail);
      saved = true;
    }

    writeList(list);

    document.dispatchEvent(
      new CustomEvent('wishlist:change', { detail: { list: list, handle: detail.handle, saved: saved } })
    );

    return saved;
  }

  /** Every heart button on the page reflects the current localStorage state. */
  function syncButtons() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(function (button) {
      var saved = isSaved(button.dataset.productHandle);
      button.classList.toggle('is-saved', saved);
      button.setAttribute('aria-pressed', String(saved));

      var label = saved ? button.dataset.removeLabel : button.dataset.addLabel;
      if (label) button.setAttribute('aria-label', label);
    });
  }

  document.addEventListener('click', function (event) {
    var toggleButton = event.target.closest('[data-wishlist-toggle]');
    if (toggleButton) {
      event.preventDefault();
      toggle({
        handle: toggleButton.dataset.productHandle,
        title: toggleButton.dataset.productTitle || '',
        price: toggleButton.dataset.productPrice || '',
        image: toggleButton.dataset.productImage || '',
        url: toggleButton.dataset.productUrl || '',
      });
      return;
    }

    if (event.target.closest('[data-wishlist-drawer-trigger]')) {
      event.preventDefault();
      document.dispatchEvent(new CustomEvent('wishlist-drawer:open'));
    }
  });

  document.addEventListener('wishlist:change', syncButtons);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncButtons);
  } else {
    syncButtons();
  }

  // A small surface other theme code can call into without reaching for
  // events, mirroring the pattern of exposing helpers off `window.Shopify`
  // elsewhere in the platform.
  window.PixelBearWishlist = {
    readList: readList,
    isSaved: isSaved,
    toggle: toggle,
  };

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (char) {
      return (
        {
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;',
        }[char] || char
      );
    });
  }

  class WishlistDrawer extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-wishlist-list]');
      this.empty = this.querySelector('[data-wishlist-empty]');
      this.removeLabel = this.dataset.removeLabel || 'Remove';

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-wishlist-drawer-close]')) {
          this.close();
          return;
        }

        const removeButton = event.target.closest('[data-wishlist-remove]');
        if (removeButton) {
          toggle({ handle: removeButton.dataset.productHandle });
        }
      });

      document.addEventListener('wishlist-drawer:open', () => this.open());
      document.addEventListener('wishlist:change', () => this.render());

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });

      this.render();
    }

    open() {
      this.hidden = false;
      document.body.classList.add('scroll-locked');
    }

    close() {
      this.hidden = true;
      document.body.classList.remove('scroll-locked');
    }

    render() {
      if (!this.list) return;

      const items = readList();

      if (this.empty) this.empty.hidden = items.length > 0;

      this.list.innerHTML = items
        .map((item) => {
          const url = item.url || '#';
          return (
            '<li class="wishlist-drawer__item">' +
            '<a href="' + escapeHtml(url) + '" class="wishlist-drawer__item-media media media--square">' +
            (item.image
              ? '<img src="' + escapeHtml(item.image) + '" alt="" loading="lazy">'
              : '') +
            '</a>' +
            '<div class="wishlist-drawer__item-info">' +
            '<a href="' + escapeHtml(url) + '" class="wishlist-drawer__item-title">' + escapeHtml(item.title) + '</a>' +
            '<span class="wishlist-drawer__item-price">' + escapeHtml(item.price) + '</span>' +
            '<button type="button" class="wishlist-drawer__remove" data-wishlist-remove data-product-handle="' +
            escapeHtml(item.handle) +
            '">' +
            escapeHtml(this.removeLabel) +
            '</button>' +
            '</div>' +
            '</li>'
          );
        })
        .join('');
    }
  }

  if (!customElements.get('wishlist-drawer')) {
    customElements.define('wishlist-drawer', WishlistDrawer);
  }
})();
